/**
 * Converts structured AI markdown into visual blocks that can be rendered by
 * the web client without changing the API response format.
 */
export type AiBlock =
    | { type: "text"; content: string }
    | { type: "table"; headers: string[]; rows: string[][] }
    | { type: "bar_chart"; title: string; items: { label: string; value: number; raw: string }[] }
    | { type: "comparison"; title: string; items: { label: string; value: string; note?: string }[] }
    | { type: "info_card"; title: string; lines: { key: string; value: string }[] };

interface Section {
    heading: string;
    lines: string[];
}

function parseTableRow(line: string): string[] {
    return line
        .replace(/^\|/, "")
        .replace(/\|$/, "")
        .split("|")
        .map(cell => cell.trim());
}

function tryParseTable(lines: string[]): Extract<AiBlock, { type: "table" }> | null {
    if (lines.length < 3 || !/^\|?[\s\-|:]+\|?$/.test(lines[1])) return null;
    const headers = parseTableRow(lines[0]);
    if (headers.length < 2) return null;
    const rows = lines.slice(2).map(parseTableRow).filter(row => row.some(Boolean));
    return rows.length ? { type: "table", headers, rows } : null;
}

const RANKED_LINE_RE =
    /^(?:\d+[.)]\s+|[-•]\s+)(.+?)(?:[:\s—–-]+)(\d+(?:[.,]\d+)?\s*(?:%|pts?|pontos?|\/\d+|stars?|estrelas?))\s*$/i;

function tryParseBarChart(
    lines: string[],
    title: string,
): Extract<AiBlock, { type: "bar_chart" }> | null {
    const items: Extract<AiBlock, { type: "bar_chart" }>["items"] = [];
    for (const line of lines) {
        const match = line.match(RANKED_LINE_RE);
        if (!match) continue;
        const raw = match[2].trim();
        const value = Number.parseFloat(raw.replace(",", ".").replace(/[^\d.]/g, ""));
        if (Number.isFinite(value)) items.push({ label: match[1].trim(), value, raw });
    }
    return items.length >= 3 ? { type: "bar_chart", title, items } : null;
}

const KEY_VALUE_RE = /^(?:\*{1,2})?(.+?)(?:\*{0,2})\s*:\s*(.+)$/;

function tryParseInfoCard(
    lines: string[],
    title: string,
): Extract<AiBlock, { type: "info_card" }> | null {
    const pairs: Extract<AiBlock, { type: "info_card" }>["lines"] = [];
    for (const line of lines) {
        if (!line.trim() || /^#+/.test(line)) continue;
        const match = line.match(KEY_VALUE_RE);
        if (match) {
            pairs.push({
                key: match[1].trim().replace(/^\*+|\*+$/g, ""),
                value: match[2].trim(),
            });
        }
    }
    return pairs.length >= 3 ? { type: "info_card", title, lines: pairs } : null;
}

const COMPARISON_RE = /^(?:\d+[.)]\s+|[-•]\s+)(.+?)(?:\s*:\s+|\s+[—–-]\s+)(.+)$/;

function tryParseComparison(
    lines: string[],
    title: string,
): Extract<AiBlock, { type: "comparison" }> | null {
    const items: Extract<AiBlock, { type: "comparison" }>["items"] = [];
    for (const line of lines) {
        const match = line.replace(/\*\*/g, "").match(COMPARISON_RE);
        if (!match) continue;
        const label = match[1].trim();
        const valueWithNote = match[2].trim();
        if (label.length > 40 || valueWithNote.length > 50) return null;
        const noteMatch = valueWithNote.match(/^(.+?)\s*\((.+?)\)$/);
        items.push(noteMatch
            ? { label, value: noteMatch[1].trim(), note: noteMatch[2].trim() }
            : { label, value: valueWithNote });
    }
    return items.length >= 3 ? { type: "comparison", title, items } : null;
}

function splitIntoSections(text: string): Section[] {
    const sections: Section[] = [];
    let current: Section = { heading: "", lines: [] };
    let inCodeFence = false;

    for (const line of text.split("\n")) {
        if (line.trim().startsWith("```")) inCodeFence = !inCodeFence;
        const heading = !inCodeFence ? line.match(/^(#{1,3})\s+(.+)$/) : null;
        if (heading) {
            if (current.heading || current.lines.some(item => item.trim())) sections.push(current);
            current = { heading: heading[2].trim(), lines: [] };
        } else {
            current.lines.push(line);
        }
    }

    if (current.heading || current.lines.some(line => line.trim())) sections.push(current);
    return sections;
}

function textBlock(heading: string, lines: string[]): Extract<AiBlock, { type: "text" }> | null {
    const content = [heading ? `**${heading}**` : "", ...lines].join("\n").trim();
    return content ? { type: "text", content } : null;
}

export function parseAiMessage(text: string): AiBlock[] {
    if (!text?.trim()) return [];
    const blocks: AiBlock[] = [];

    for (const section of splitIntoSections(text)) {
        const nonEmpty = section.lines.filter(line => line.trim());

        // Code samples must remain untouched markdown, even when they contain
        // table pipes or several key:value lines.
        if (section.lines.some(line => line.trim().startsWith("```"))) {
            const codeFallback = textBlock(section.heading, section.lines);
            if (codeFallback) blocks.push(codeFallback);
            continue;
        }

        const firstTableLine = section.lines.findIndex(line => line.trim().startsWith("|"));

        if (firstTableLine >= 0) {
            let tableEnd = firstTableLine;
            while (tableEnd < section.lines.length && section.lines[tableEnd].trim().startsWith("|")) {
                tableEnd += 1;
            }
            const table = tryParseTable(section.lines.slice(firstTableLine, tableEnd));
            if (table) {
                const before = textBlock(section.heading, section.lines.slice(0, firstTableLine));
                if (before) blocks.push(before);
                blocks.push(table);
                const after = textBlock("", section.lines.slice(tableEnd));
                if (after) blocks.push(after);
                continue;
            }
        }

        const chart = tryParseBarChart(nonEmpty, section.heading);
        if (chart) {
            blocks.push(chart);
            continue;
        }

        const comparison = tryParseComparison(nonEmpty, section.heading);
        if (comparison) {
            blocks.push(comparison);
            continue;
        }

        const infoCard = tryParseInfoCard(nonEmpty, section.heading);
        if (infoCard) {
            blocks.push(infoCard);
            continue;
        }

        const fallback = textBlock(section.heading, section.lines);
        if (fallback) blocks.push(fallback);
    }

    return blocks;
}

export function hasVisualBlocks(blocks: AiBlock[]): boolean {
    return blocks.some(block => block.type !== "text");
}
